import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  TableContainer,
  TablePagination,
  Paper,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import AsyncSearch from "components/AsyncSearch";
import { useMaterialUIController } from "../../context";

const DEFAULT_ROOT_PLACE_ID = "2e606b67-2689-473d-95fc-7b9820438ff0";

const formatDateTime = (value) =>
  value ? new Date(value * 1000).toLocaleString("he-IL", { hour12: false }) : "-";

function AddUsers() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [place, setPlace] = useState({ id: "", name: "" });
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState({});
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const rowsWithKeys = useMemo(
    () =>
      logs.map((row, idx) => ({
        ...row,
        rowKey: row.opId || `${row.carId || "car"}-${idx}`,
      })),
    [logs]
  );

  const pagedRows = useMemo(
    () =>
      rowsWithKeys.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rowsWithKeys, page, rowsPerPage]
  );

  const allSelected = rowsWithKeys.length > 0 && rowsWithKeys.every((item) => selected[item.rowKey]);
  const someSelected = rowsWithKeys.some((item) => selected[item.rowKey]) && !allSelected;

  const toggleAll = (checked) => {
    if (!logs.length) return;
    if (checked) {
      const next = {};
      rowsWithKeys.forEach((l) => {
        next[l.rowKey] = l;
      });
      setSelected(next);
    } else {
      setSelected({});
    }
  };

  const toggleOne = (row) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[row.rowKey]) {
        delete next[row.rowKey];
      } else {
        next[row.rowKey] = row;
      }
      return next;
    });
  };

  const fetchLogs = async () => {
    setError("");
    setInfo("");
    if (!place.id || !dateRange.from || !dateRange.to) {
      setError("יש לבחור חניון וטווח תאריכים");
      return;
    }

    const start = Math.floor(new Date(dateRange.from).getTime() / 1000);
    const end = Math.floor(new Date(dateRange.to).getTime() / 1000);

    setLoadingLogs(true);
    try {
      const params = new URLSearchParams({
        placeId: place.id,
        startDate: String(start),
        endDate: String(end),
        skip: "0",
        limit: "50",
        reasons: "paid discount",
      });
      const resp = await fetch(`https://blue-parking-invite.bluept.dev/api/pal/logs?${params.toString()}`);
      if (!resp.ok) {
        const body = await resp.text();
        throw new Error(body || "log fetch failed");
      }
      const json = await resp.json();
      const list = json?.log?.list || [];
      setLogs(list);
      setSelected({});
      if (!list.length) setInfo("לא נמצאו תוצאות עבור הטווח שנבחר");
    } catch (e) {
      setError(e?.message || "שגיאה בשליפת הלוגים");
    } finally {
      setLoadingLogs(false);
      setPage(0);
    }
  };

  const handleAdd = async () => {
    setError("");
    setInfo("");
    const rows = Object.values(selected);
    if (!place.id) {
      setError("יש לבחור חניון");
      return;
    }
    if (!rows.length) {
      setError("בחר לפחות רכב אחד");
      return;
    }

    setAdding(true);
    try {
      const payload = {
        users: rows.map((r) => ({
          carId: r.carId,
          uid: r.uid,
          firstname: r.firstname || r.placeName || "",
        })),
        maxStayTime: 7200,
      };
      const resp = await fetch(
        `https://blue-parking-invite.bluept.dev/api/pal/places/${encodeURIComponent(place.id)}/users/bulk-add`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!resp.ok) {
        const body = await resp.text();
        throw new Error(body || "הוספת מנויים נכשלה");
      }
      const json = await resp.json();
      const failList = json.failed?.map((f) => f.carId).filter(Boolean) || [];
      const successMsg = `נוספו ${json.successCount || 0} רכבים בהצלחה`;
      const failMsg = failList.length ? `; נכשלו: ${failList.join(", ")}` : "";
      setInfo(successMsg + failMsg);
    } catch (e) {
      setError(e?.message || "הוספת מנויים נכשלה");
    } finally {
      setAdding(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox p={2}>
        <MDTypography variant="h4" gutterBottom>
          הוספת מנויים ששילמו בהנחה
        </MDTypography>

        <Card
          style={{
            padding: 16,
            marginBottom: 16,
            backgroundColor: darkMode ? "#1e293b" : "#fff",
            color: darkMode ? "#fff" : "#000",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <AsyncSearch
                label="חניון (Pal-ES)"
                value={place.name}
                searchUrl="https://blue-parking-invite.bluept.dev/api/search-place/"
                rootPlaceId={DEFAULT_ROOT_PLACE_ID}
                onValueChange={(name, id) => setPlace({ name, id })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="מתאריך"
                type="datetime-local"
                fullWidth
                value={dateRange.from}
                onChange={(e) => setDateRange((prev) => ({ ...prev, from: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="עד תאריך"
                type="datetime-local"
                fullWidth
                value={dateRange.to}
                onChange={(e) => setDateRange((prev) => ({ ...prev, to: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2} display="flex" alignItems="center">
              <Button variant="contained" color="primary" onClick={fetchLogs} fullWidth>
                {loadingLogs ? <CircularProgress size={20} /> : "טען לוגים"}
              </Button>
            </Grid>
          </Grid>
          {error && (
            <MDBox mt={2}>
              <Alert severity="error">{error}</Alert>
            </MDBox>
          )}
          {info && (
            <MDBox mt={2}>
              <Alert severity="info">{info}</Alert>
            </MDBox>
          )}
        </Card>

        <Card
          style={{
            padding: 16,
            backgroundColor: darkMode ? "#1e293b" : "#fff",
            color: darkMode ? "#fff" : "#000",
          }}
        >
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            style={{ marginBottom: 8 }}
          >
            <Grid item>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={(e) => toggleAll(e.target.checked)}
                  />
                }
                label="בחר הכל"
              />
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleAdd}
                disabled={adding || !Object.keys(selected).length}
              >
                {adding ? <CircularProgress size={20} /> : "הוסף כמנוי לשעתיים"}
              </Button>
            </Grid>
          </Grid>

          <TableContainer component={Paper} style={{   display: "table-header-group", backgroundColor: darkMode ? "#0f172a" : "#fff" }}>
            <Table size="small" stickyHeader>
              <TableHead sx={{  display: "table-header-group"}}>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ color: darkMode ? "#fff" : "#000" }}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={(e) => toggleAll(e.target.checked)}
                    />
                  </TableCell>
                  <TableCell sx={{ color: darkMode ? "#fff" : "#000" }}>Car ID</TableCell>
                  <TableCell sx={{ color: darkMode ? "#fff" : "#000" }}>User ID</TableCell>
                  <TableCell sx={{ color: darkMode ? "#fff" : "#000" }}>Direction</TableCell>
                  <TableCell sx={{ color: darkMode ? "#fff" : "#000" }}>Reason</TableCell>
                  <TableCell sx={{ color: darkMode ? "#fff" : "#000" }}>Time</TableCell>
                  <TableCell sx={{ color: darkMode ? "#fff" : "#000" }}>Place</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((row) => (
                  <TableRow key={row.rowKey} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={!!selected[row.rowKey]}
                        onChange={() => toggleOne(row)}
                      />
                    </TableCell>
                    <TableCell sx={{ color: darkMode ? "#fff" : "#000" }}>{row.carId}</TableCell>
                    <TableCell sx={{ color: darkMode ? "#fff" : "#000" }}>{row.uid}</TableCell>
                    <TableCell sx={{ color: darkMode ? "#fff" : "#000" }}>{row.direction}</TableCell>
                    <TableCell sx={{ color: darkMode ? "#fff" : "#000" }}>{row.reason}</TableCell>
                    <TableCell sx={{ color: darkMode ? "#fff" : "#000" }}>{formatDateTime(row.tm)}</TableCell>
                    <TableCell sx={{ color: darkMode ? "#fff" : "#000" }}>{row.placeName}</TableCell>
                  </TableRow>
                ))}
                {!rowsWithKeys.length && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography align="center" sx={{ color: darkMode ? "#fff" : "#000" }}>
                        אין נתונים להצגה
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={rowsWithKeys.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                color: darkMode ? "#fff" : "#000",
                ".MuiSelect-icon": { color: darkMode ? "#fff" : "#000" },
              }}
            />
          </TableContainer>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default AddUsers;
